import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, take, tap } from 'rxjs/operators';

export type LocalizationDictionary = { [key: string]: string | undefined };

@Injectable({
    providedIn: 'root',
})
export class LocalizationService
{
    // state
    _currentLanguageOption!: LanguageOption;
    _allLanguageOptionsByIds: { [key: string]: LanguageOption } = {};
    _currentTermsByKey: LocalizationDictionary = {};
    private _termsSubject = new BehaviorSubject<LocalizationDictionary>({});
    Terms$ = this._termsSubject.asObservable();

    private _dictionaryCache: { [languageId: string]: LocalizationDictionary } = {};
    private _dictionaryLoaders: { [languageId: string]: Observable<LocalizationDictionary> } = {};

    // config
    private _languagePreferenceIdKey = 'config-language-preference-id';
    private _defaultLanguageId = 'en-us';
    private _languageAliasMap: { [key: string]: string } =
        {
            'en': 'en-us',
            'en-gb': 'en-us',
            'en-ca': 'en-us',
            'en-au': 'en-us',
            'pt': 'pt-br',
            'pt-pt': 'pt-br',
            'pt-br': 'pt-br',
            'es': 'es-es',
            'es-es': 'es-es',
            'es-mx': 'es-es',
            'es-ar': 'es-es',
        };

    _allLanguageOptions: LanguageOption[] = [
        {
            Id: 'en-us',
            Name: 'English',
            FlagIconName: 'usa-flag-icon-01',
            ShortCode: 'EN',
        },
        {
            Id: 'pt-br',
            Name: 'Portugues',
            FlagIconName: 'brazil-flag-icon-01',
            ShortCode: 'PT',
        },
        {
            Id: 'es-es',
            Name: 'Espanol',
            FlagIconName: 'spain-flag-icon-01',
            ShortCode: 'ES',
        },
    ];

    constructor(private _http: HttpClient)
    {
        this.SetInitialState();
    }

    SetInitialState = () =>
    {
        this._allLanguageOptionsByIds = {};
        this._dictionaryCache = {};

        this._allLanguageOptions.forEach(
            (languageOption) =>
            {
                if (!(languageOption.Id in this._allLanguageOptionsByIds))
                    this._allLanguageOptionsByIds[languageOption.Id] = languageOption;
                else
                    console.warn('Duplicated languageOption.Id detected.', languageOption);
            });

        const initialLanguageOption = this.GetCurrentLanguageOptionCache();

        if (initialLanguageOption.Id !== this._defaultLanguageId)
            this.LoadLanguageDictionary(this._defaultLanguageId).pipe(take(1)).subscribe();

        this.ApplyLanguageOption(initialLanguageOption);
    };

    Translate = (termKey: string, fallback?: string) =>
    {
        if (termKey in this._currentTermsByKey)
            return this._currentTermsByKey[termKey];

        const defaultDictionary = this._dictionaryCache[this._defaultLanguageId];
        if (defaultDictionary && termKey in defaultDictionary)
            return defaultDictionary[termKey];

        return this.BuildMissingTermMessage(termKey, fallback);
    };

    SetCurrentLanguage = (languageId: string) =>
    {
        const normalizedLanguageId = this.NormalizeLanguageId(languageId) ?? this._defaultLanguageId;
        const resolvedLanguageOption = this.GetLanguageOptionOrDefault(normalizedLanguageId);

        this.TryPersistLanguage(resolvedLanguageOption.Id);
        this.ApplyLanguageOption(resolvedLanguageOption);
    };

    GetCurrentLanguageOptionCache = () =>
    {
        const storedLanguageId = this.TryGetStoredLanguageId();
        const browserLanguageId = this.TryGetBrowserLanguageId();

        const candidateLanguageId =
            storedLanguageId ??
            browserLanguageId ??
            this._defaultLanguageId;

        const resolvedLanguageOption = this.GetLanguageOptionOrDefault(candidateLanguageId);

        if (!storedLanguageId)
            this.TryPersistLanguage(resolvedLanguageOption.Id);

        return resolvedLanguageOption;
    };

    private ApplyLanguageOption = (languageOption: LanguageOption) =>
    {
        this.LoadLanguageDictionary(languageOption.Id)
            .pipe(take(1))
            .subscribe((dictionary) =>
            {
                this._currentLanguageOption = languageOption;
                this._currentTermsByKey = dictionary;
                this._termsSubject.next(this._currentTermsByKey);
            });
    };

    private LoadLanguageDictionary = (languageId: string): Observable<LocalizationDictionary> =>
    {
        const resolvedLanguageId = this.NormalizeLanguageId(languageId) ?? this._defaultLanguageId;
        const resolvedOption = this.GetLanguageOptionOrDefault(resolvedLanguageId);
        const finalLanguageId = resolvedOption.Id;

        if (finalLanguageId in this._dictionaryCache)
            return of(this._dictionaryCache[finalLanguageId]);

        if (finalLanguageId in this._dictionaryLoaders)
            return this._dictionaryLoaders[finalLanguageId];

        const assetUrl = `assets/i18n/${finalLanguageId}.json`;

        const loader = this._http.get<LocalizationDictionary>(assetUrl).pipe(
            tap((dictionary) =>
            {
                this._dictionaryCache[finalLanguageId] = dictionary ?? {};
            }),
            map(() => this._dictionaryCache[finalLanguageId]),
            catchError((error) =>
            {
                console.warn(`Unable to load dictionary for language '${finalLanguageId}'.`, error);
                this._dictionaryCache[finalLanguageId] = {};
                return of(this._dictionaryCache[finalLanguageId]);
            }),
            shareReplay(1),
        );

        this._dictionaryLoaders[finalLanguageId] = loader;

        return loader;
    };

    private BuildMissingTermMessage = (termKey: string, fallback?: string) =>
    {
        const fallbackValue = fallback ?? termKey;
        return '[missing term ' + termKey + '] ' + fallbackValue;
    };

    private GetLanguageOptionOrDefault = (languageId: string | undefined) =>
    {
        const normalizedLanguageId = this.NormalizeLanguageId(languageId);

        if (normalizedLanguageId && normalizedLanguageId in this._allLanguageOptionsByIds)
            return this._allLanguageOptionsByIds[normalizedLanguageId];

        if (this._defaultLanguageId in this._allLanguageOptionsByIds)
            return this._allLanguageOptionsByIds[this._defaultLanguageId];

        return this._allLanguageOptions[0];
    };

    private NormalizeLanguageId = (languageId?: string | null) =>
    {
        if (!languageId)
            return undefined;

        const sanitizedLanguageId = languageId.toLowerCase();

        if (sanitizedLanguageId in this._allLanguageOptionsByIds)
            return sanitizedLanguageId;

        if (sanitizedLanguageId in this._languageAliasMap)
            return this._languageAliasMap[sanitizedLanguageId];

        const prefixLanguageId = sanitizedLanguageId.split('-')[0];

        if (prefixLanguageId in this._languageAliasMap)
            return this._languageAliasMap[prefixLanguageId];

        if (prefixLanguageId in this._allLanguageOptionsByIds)
            return prefixLanguageId;

        return undefined;
    };

    private TryGetStoredLanguageId = () =>
    {
        if (typeof window === 'undefined' || !window?.localStorage)
            return undefined;

        try
        {
            const storedValue = window.localStorage.getItem(this._languagePreferenceIdKey);
            return this.NormalizeLanguageId(storedValue ?? undefined);
        }
        catch (error)
        {
            console.warn('Unable to read language from localStorage.', error);
            return undefined;
        }
    };

    private TryPersistLanguage = (languageId: string) =>
    {
        if (typeof window === 'undefined' || !window?.localStorage)
            return;

        try
        {
            window.localStorage.setItem(this._languagePreferenceIdKey, languageId);
        }
        catch (error)
        {
            console.warn('Unable to persist language preference.', error);
        }
    };

    private TryGetBrowserLanguageId = () =>
    {
        if (typeof navigator === 'undefined')
            return undefined;

        const navigatorLanguages: string[] = [];

        if (Array.isArray(navigator.languages))
            navigatorLanguages.push(...navigator.languages);

        if (navigator.language)
            navigatorLanguages.push(navigator.language);

        for (const candidate of navigatorLanguages)
        {
            const normalizedCandidate = this.NormalizeLanguageId(candidate);

            if (normalizedCandidate)
                return normalizedCandidate;
        }

        return undefined;
    };
}

export interface LanguageOption
{
    Id: string,
    Name: string,
    FlagIconName: string,
    ShortCode: string,
}
